import {TileCorner, TileEdge, TileHex} from './Tile'
import {HexCorner, HexEdge, HexFace} from '@colonist/game-mechanic-validators'
import {PlayerColor, TileCornerBuildingType, TileCornerStates, TileEdgeStates, TileEdgeType, TileHexStates, TileHexType} from '@colonist/game-states'

export class TileState {
    private readonly _tiles: TileHex[]
    private readonly _tileCorners: TileCorner[]
    private readonly _tileEdges: TileEdge[]

    private readonly tileHexStates: TileHexStates
    private readonly tileCornerStates: TileCornerStates
    private readonly tileEdgeStates: TileEdgeStates

    private readonly tileHexIndexes: number[][]
    private readonly tileCornerIndexes: number[][][]
    private readonly tileEdgeIndexes: number[][][]

    constructor(tileHexStates: TileHexStates, tileCornerStates: TileCornerStates, tileEdgeStates: TileEdgeStates) {
        this._tiles = []
        this._tileCorners = []
        this._tileEdges = []

        this.tileHexStates = tileHexStates
        this.tileCornerStates = tileCornerStates
        this.tileEdgeStates = tileEdgeStates

        this.tileHexIndexes = []
        this.tileCornerIndexes = []
        this.tileEdgeIndexes = []

        this.loadMap()
    }

    get tiles(): readonly TileHex[] {
        return this._tiles
    }

    get tileCorners(): readonly TileCorner[] {
        return this._tileCorners
    }

    get tileEdges(): readonly TileEdge[] {
        return this._tileEdges
    }

    addNewTile(tile: TileHex): number {
        this._tiles.push(tile)
        const index = this.tiles.length - 1
        this.tileHexStates[index] = tile.state
        this.indexTileHex(tile, index)
        return index
    }

    addNewCorner(tileCorner: TileCorner) {
        this._tileCorners.push(tileCorner)
        const index = this.tileCorners.length - 1
        this.tileCornerStates[index] = tileCorner.state
        this.indexMapCorner(tileCorner, index)
    }

    addNewEdge(tileEdge: TileEdge) {
        this._tileEdges.push(tileEdge)
        const index = this.tileEdges.length - 1
        this.tileEdgeStates[index] = tileEdge.state
        this.indexMapEdges(tileEdge, index)
    }

    private loadMap() {
        this.loadMapTiles()
        this.loadMapCorners()
        this.loadMapEdges()
    }

    private loadMapTiles() {
        for(const index in this.tileHexStates) {
            const tileState = this.tileHexStates[index]
            if(tileState == undefined) continue

            const tileHex = new TileHex(tileState)
            this._tiles.push(tileHex)

            const arrayIndex = this.tiles.length - 1
            this.indexTileHex(tileHex, arrayIndex)
        }
    }

    private indexTileHex(tileHex: TileHex, index: number) {
        const xIndexLayer = this.tileHexIndexes[tileHex.hexFace.x] ?? []

        xIndexLayer[tileHex.hexFace.y] = index
        this.tileHexIndexes[tileHex.hexFace.x] = xIndexLayer
    }

    private loadMapCorners() {
        for(const index in this.tileCornerStates) {
            const tileCornerState = this.tileCornerStates[index]
            if(tileCornerState == undefined) continue

            const tileCorner = new TileCorner(tileCornerState)
            this._tileCorners.push(tileCorner)

            const arrayIndex = this.tileCorners.length - 1
            this.indexMapCorner(tileCorner, arrayIndex)
        }
    }

    private indexMapCorner(tileCorner: TileCorner, index: number) {
        const xIndexLayer = this.tileCornerIndexes[tileCorner.hexCorner.x] ?? []
        const yIndexLayer = xIndexLayer[tileCorner.hexCorner.y] ?? []

        yIndexLayer[tileCorner.hexCorner.z] = index
        xIndexLayer[tileCorner.hexCorner.y] = yIndexLayer
        this.tileCornerIndexes[tileCorner.hexCorner.x] = xIndexLayer
    }

    private loadMapEdges() {
        for(const index in this.tileEdgeStates) {
            const tileEdgeState = this.tileEdgeStates[index]
            if(tileEdgeState == undefined) continue

            const tileEdge = new TileEdge(tileEdgeState)
            this._tileEdges.push(tileEdge)

            const arrayIndex = this.tileEdges.length - 1
            this.indexMapEdges(tileEdge, arrayIndex)
        }
    }

    private indexMapEdges(tileEdge: TileEdge, index: number) {
        const xIndexLayer = this.tileEdgeIndexes[tileEdge.hexEdge.x] ?? []
        const yIndexLayer = xIndexLayer[tileEdge.hexEdge.y] ?? []

        yIndexLayer[tileEdge.hexEdge.z] = index
        xIndexLayer[tileEdge.hexEdge.y] = yIndexLayer
        this.tileEdgeIndexes[tileEdge.hexEdge.x] = xIndexLayer
    }

    // ====================================================================================================================================
    // Getters: Tile index
    // ====================================================================================================================================

    getTileHexIndex(hexFace: HexFace): number | undefined {
        const xIndexLayer = this.tileHexIndexes[hexFace.x]
        if(xIndexLayer == undefined) return undefined
        return xIndexLayer[hexFace.y]
    }

    getTileCornerIndex(hexCorner: HexCorner): number | undefined {
        const xIndexLayer = this.tileCornerIndexes[hexCorner.x]
        if(xIndexLayer == undefined) return undefined

        const yIndexLayer = xIndexLayer[hexCorner.y]
        if(yIndexLayer == undefined) return undefined

        return yIndexLayer[hexCorner.z]
    }

    getTileEdgeIndex(hexEdge: HexEdge): number | undefined {
        const xIndexLayer = this.tileEdgeIndexes[hexEdge.x]
        if(xIndexLayer == undefined) return undefined

        const yIndexLayer = xIndexLayer[hexEdge.y]
        if(yIndexLayer == undefined) return undefined

        return yIndexLayer[hexEdge.z]
    }

    // ====================================================================================================================================
    // Getters: Tile parts
    // ====================================================================================================================================

    getTileHex(hexFace: HexFace): TileHex | undefined {
        const tileHexIndex = this.getTileHexIndex(hexFace)
        if(tileHexIndex == undefined) return undefined

        const tileHex = this.tiles[tileHexIndex]
        if(tileHex == undefined) return undefined

        if(!tileHex.hexFace.sameFace(hexFace)) return undefined

        return tileHex
    }

    getTileCorner(hexCorner: HexCorner): TileCorner | undefined {
        const tileCornerIndex = this.getTileCornerIndex(hexCorner)
        if(tileCornerIndex == undefined) return undefined

        const tileCorner = this.tileCorners[tileCornerIndex]
        if(tileCorner == undefined) return undefined

        if(!tileCorner.hexCorner.sameCorner(hexCorner)) return undefined

        return tileCorner
    }

    getTileEdge(hexEdge: HexEdge): TileEdge | undefined {
        const tileEdgeIndex = this.getTileEdgeIndex(hexEdge)
        if(tileEdgeIndex == undefined) return undefined

        const tileEdge = this.tileEdges[tileEdgeIndex]
        if(tileEdge == undefined) return undefined

        if(!tileEdge.hexEdge.sameEdge(hexEdge)) return undefined

        return tileEdge
    }

    // ====================================================================================================================================
    // Are parts touching land
    // ====================================================================================================================================

    isEdgeTouchingLand(tileEdge: TileEdge): boolean {
        for(const touchingFace of tileEdge.hexEdge.touchingFaces()) {
            const touchingTile = this.getTileHex(touchingFace)
            if(touchingTile == undefined) continue

            if(touchingTile.isTileLand()) return true
        }
        return false
    }

    isCornerTouchingLand(tileCorner: TileCorner): boolean {
        for(const touchingFace of tileCorner.hexCorner.touchingFaces()) {
            const touchingTile = this.getTileHex(touchingFace)
            if(touchingTile == undefined) continue

            if(touchingTile.isTileLand()) return true
        }
        return false
    }

    // ====================================================================================================================================
    // Are parts touching sea
    // ====================================================================================================================================

    isEdgeTouchingSea(tileEdge: TileEdge): boolean {
        for(const touchingFace of tileEdge.hexEdge.touchingFaces()) {
            const touchingTile = this.getTileHex(touchingFace)

            if(touchingTile == undefined) return true
            if(touchingTile.tileType == TileHexType.Sea) return true
        }
        return false
    }

    isCornerTouchingSea(tileCorner: TileCorner): boolean {
        for(const touchingFace of tileCorner.hexCorner.touchingFaces()) {
            const touchingTile = this.getTileHex(touchingFace)

            if(touchingTile == undefined) return true
            if(touchingTile.tileType == TileHexType.Sea) return true
        }
        return false
    }

    // ====================================================================================================================================
    // Are parts on shore
    // ====================================================================================================================================

    isEdgeOnShore(hexEdge: HexEdge): boolean {
        const tileEdge = this.getTileEdge(hexEdge)
        if(tileEdge == undefined) return false

        if(!this.isEdgeTouchingLand(tileEdge)) return false
        if(!this.isEdgeTouchingSea(tileEdge)) return false

        return true
    }

    isCornerOnShore(hexCorner: HexCorner) {
        const tileCorner = this.getTileCorner(hexCorner)
        if(tileCorner == undefined) return false

        if(!this.isCornerTouchingLand(tileCorner)) return false
        if(!this.isCornerTouchingSea(tileCorner)) return false

        return true
    }

    isTileOnShore(tileHex: TileHex): boolean {
        if(!tileHex.isTileLand()) return false
        for(const neightbor of tileHex.hexFace.neighbors()) {
            const neighboringTile = this.getTileHex(neightbor)
            if(neighboringTile == undefined) return true
            if(neighboringTile.tileType == TileHexType.Sea) return true
        }
        return false
    }

    // ====================================================================================================================================
    // Tile pieces
    // ====================================================================================================================================

    getPlacedEdgePieceCountForPlayer(edgePieceType: TileEdgeType, player: PlayerColor): number {
        let count = 0
        for(const tileEdgeStateKey in this.tileEdgeStates) {
            const tileEdgeState = this.tileEdgeStates[tileEdgeStateKey]
            if(tileEdgeState == undefined) continue

            if(tileEdgeState.type != edgePieceType) continue
            if(tileEdgeState.owner != player) continue

            count += 1
        }

        return count
    }

    getPlacedCornerBuildingCountForPlayer(cornerBuildingType: TileCornerBuildingType, player: PlayerColor): number {
        let count = 0
        for(const tileCornerStateKey in this.tileCornerStates) {
            const tileCornerState = this.tileCornerStates[tileCornerStateKey]
            if(tileCornerState == undefined) continue

            if(tileCornerState.buildingType != cornerBuildingType) continue
            if(tileCornerState.owner != player) continue

            count += 1
        }

        return count
    }
}
